import { FC } from 'react';
import { Box, Paper, Stack, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { Trans, useTrans } from '@libs/i18n';
import { ArrowRightIcon, ArrowsClockwiseIcon } from '@phosphor-icons/react';

interface ReassessmentBannerProps {
  currentVersion: number;
  parentVersion: number;
  parentSubmittedAt: string | null;
}

const formatShortDate = (iso: string, locale: string): string =>
  new Date(iso).toLocaleDateString(locale, { month: 'short', day: 'numeric' });

export const ReassessmentBanner: FC<ReassessmentBannerProps> = observer(
  ({ currentVersion, parentVersion, parentSubmittedAt }) => {
    const theme = useTheme();
    const { i18n } = useTrans();

    const fromChipLabel = parentSubmittedAt ? (
      <Trans
        i18nKey="app.assessment.workflow.reassessmentBanner.fromVersionWithDate"
        values={{
          parentVersion,
          parentSubmittedDate: formatShortDate(parentSubmittedAt, i18n.language),
        }}
      />
    ) : (
      <Trans
        i18nKey="app.assessment.workflow.reassessmentBanner.fromVersion"
        values={{ parentVersion }}
      />
    );

    return (
      <Paper
        variant="panel"
        elevation={0}
        sx={{
          backgroundColor: 'purple.100',
          border: `1px solid ${theme.palette.purple[200]}`,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              backgroundColor: 'purple.700',
              p: 1.5,
              borderRadius: '12px',
              display: 'flex',
              flexShrink: 0,
            }}
          >
            <ArrowsClockwiseIcon color={theme.palette.common.white} size={22} />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap">
              <Typography variant="textMD_medium" sx={{ color: 'text.primary' }}>
                <Trans i18nKey="app.assessment.workflow.reassessmentBanner.title" />
              </Typography>

              <Box
                sx={{
                  px: 1,
                  py: 0.25,
                  borderRadius: 999,
                  border: `1px solid ${theme.palette.purple[200]}`,
                  backgroundColor: 'common.white',
                  color: 'purple.700',
                  fontSize: 12,
                  fontWeight: 600,
                  lineHeight: 1.5,
                }}
              >
                {fromChipLabel}
              </Box>

              <ArrowRightIcon size={14} color={theme.palette.text.secondary} />

              <Box
                sx={{
                  px: 1,
                  py: 0.25,
                  borderRadius: 999,
                  backgroundColor: 'purple.700',
                  color: 'common.white',
                  fontSize: 12,
                  fontWeight: 600,
                  lineHeight: 1.5,
                }}
              >
                <Trans
                  i18nKey="app.assessment.workflow.reassessmentBanner.toVersion"
                  values={{ currentVersion }}
                />
              </Box>
            </Stack>

            <Typography variant="textSM_regular" sx={{ color: 'text.secondary' }}>
              <Trans
                i18nKey="app.assessment.workflow.reassessmentBanner.description"
                values={{ parentVersion }}
              />
            </Typography>
          </Box>
        </Box>
      </Paper>
    );
  },
);
